import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useMeetingContext } from "../../../../../context/MeetingContext";
import { FaBold, FaItalic, FaAlignLeft, FaAlignCenter } from "react-icons/fa";
import { Play, Pause } from "lucide-react";
import {
  createPrompter,
  getPrompterFile,
} from "../../../../../services/elementServices";
import logger from '../../../../../utils/logger';
import { toast } from "react-toastify";

export default function Prompter() {
  const { folderId } = useParams();
  const navigate = useNavigate();
  const {
    name,
    setName,
    updatePresetId,
    setUpdatePresetId,
    onClosePanel,
    setSelectedCreateElement,
  } = useMeetingContext();

  const [selectedLayout, setSelectedLayout] = useState("");
  const [selectedTheme, setSelectedTheme] = useState("");
  const [hasConfirmedSelection, setHasConfirmedSelection] = useState(false);
  const [formData, setFormData] = useState({
    text: "Type your script here... your changes will instantly appear in the preview.",
    font: "Helvetica",
    fontSize: 23,
    isBold: false,
    isItalic: false,
    textColor: "#000000",
    speed: 0,
    layout: "compact",
    theme: "light",
    textAlign: "left",
    elementName: "",
  });
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const previewRef = useRef(null);
  const previewContentRef = useRef(null);
  const scrollIntervalRef = useRef(null);
  const hasStartedPlayingRef = useRef(false);

  // Reset play state when text changes
  useEffect(() => {
    hasStartedPlayingRef.current = false;
    if (isPlaying) {
      setIsPlaying(false);
      if (scrollIntervalRef.current) {
        clearInterval(scrollIntervalRef.current);
        scrollIntervalRef.current = null;
      }
    }
    // Reset padding when text changes
    if (previewContentRef.current) {
      previewContentRef.current.style.paddingTop = "1rem";
      previewContentRef.current.style.paddingBottom = "1rem";
    }
    // Reset scroll position when text changes
    if (previewRef.current) {
      previewRef.current.scrollTop = 0;
    }
  }, [formData.text]);

  // Reset state when creating new element (no updatePresetId)
  useEffect(() => {
    if (!updatePresetId) {
      // Reset to initial state for new element creation
      setSelectedLayout("");
      setSelectedTheme("");
      setHasConfirmedSelection(false);
      hasStartedPlayingRef.current = false;
      setIsPlaying(false);
      if (scrollIntervalRef.current) {
        clearInterval(scrollIntervalRef.current);
        scrollIntervalRef.current = null;
      }
      // Reset form data to defaults
      setFormData({
        text: "Type your script here... your changes will instantly appear in the preview.",
        font: "Helvetica",
        fontSize: 23,
        isBold: false,
        isItalic: false,
        textColor: "#000000",
        speed: 0,
        layout: "compact",
        theme: "light",
        textAlign: "left",
        elementName: "",
      });
      // Reset padding and scroll
      if (previewContentRef.current) {
        previewContentRef.current.style.paddingTop = "1rem";
        previewContentRef.current.style.paddingBottom = "1rem";
      }
      if (previewRef.current) {
        previewRef.current.scrollTop = 0;
      }
    }
  }, [updatePresetId]);

  // Load existing prompter data when editing
  useEffect(() => {
    const loadPrompterData = async () => {
      if (updatePresetId && folderId) {
        try {
          setIsLoading(true);
          const response = await getPrompterFile(updatePresetId);
          const prompterElement = response?.data || null;
          if (prompterElement) {
            const settings = JSON.parse(prompterElement.settings || "{}");

            // Set all settings from settings object
            setFormData({
              text: settings.text || "",
              font: settings.font || formData.font,
              fontSize: settings.fontSize || formData.fontSize,
              isBold: settings.isBold !== undefined ? settings.isBold : false,
              isItalic:
                settings.isItalic !== undefined ? settings.isItalic : false,
              textColor: settings.textColor || formData.textColor,
              speed: settings.speed !== undefined ? settings.speed : 0,
              layout: settings.layout || "compact",
              theme: settings.theme || "light",
              textAlign: settings.textAlign || "left",
              elementName: prompterElement.name || settings.elementName || "",
            });

            // Set selected layout and theme from settings
            const loadedLayout = settings.layout || "compact";
            const loadedTheme = settings.theme || "light";
            setSelectedLayout(loadedLayout);
            setSelectedTheme(loadedTheme);

            setHasConfirmedSelection(true); // Auto-confirm to go directly to editor when editing
            if (prompterElement.name) {
              setName(prompterElement.name);
            }
          }
        } catch (error) {
          logger("error", "Error loading prompter data:", error);
        } finally {
          setIsLoading(false);
        }
      }
    };
    loadPrompterData();
  }, [updatePresetId, folderId]);

  const handleLayoutSelect = (layout) => {
    setSelectedLayout(layout);
    // If theme is already selected, automatically proceed to editor
    if (selectedTheme) {
      setFormData((prev) => ({
        ...prev,
        layout,
        theme: selectedTheme,
      }));
      setHasConfirmedSelection(true);
    } else {
      setFormData((prev) => ({ ...prev, layout }));
    }
  };

  const handleThemeSelect = (theme) => {
    setSelectedTheme(theme);
    // If layout is already selected, automatically proceed to editor
    if (selectedLayout) {
      setFormData((prev) => ({
        ...prev,
        layout: selectedLayout,
        theme,
      }));
      setHasConfirmedSelection(true);
    } else {
      setFormData((prev) => ({ ...prev, theme }));
    }
  };

  const handleBackToSelection = () => {
    // Allow going back to selection screen (similar to CallToAction pattern)
    // Keep the loaded data but reset to selection view
    setHasConfirmedSelection(false);
  };

  // Handle text alignment
  const handleTextAlign = (align) => {
    setFormData((prev) => ({ ...prev, textAlign: align }));
  };

  // Handle play/pause with scrolling
  const handlePlayPause = () => {
    if (isPlaying) {
      // Pause: stop scrolling but keep current position
      if (scrollIntervalRef.current) {
        clearInterval(scrollIntervalRef.current);
        scrollIntervalRef.current = null;
      }
      setIsPlaying(false);
    } else {
      // Play: resume from current position or start from beginning if first time
      if (previewRef.current) {
        // Only reset to start if this is the first time playing
        if (!hasStartedPlayingRef.current) {
          previewRef.current.scrollTop = 0;
          hasStartedPlayingRef.current = true;
        }
        // Otherwise, continue from current scroll position
        setIsPlaying(true);
      }
    }
  };

  // Effect to handle scrolling animation
  useEffect(() => {
    if (isPlaying && previewRef.current && formData.speed > 0) {
      // Calculate scroll speed for smoother animation
      // Speed is 0-100, convert to pixels per second, then divide by frames per second
      // Using smaller increments with more frequent updates for smoother scrolling
      const pixelsPerSecond = (formData.speed / 100) * 200; // Max 200px per second at speed 100
      const fps = 60; // 60 frames per second for smooth animation
      const scrollAmount = pixelsPerSecond / fps; // Pixels per frame
      const intervalTime = 1000 / fps; // ~16.67ms for 60fps

      // Set initial padding when starting to play for marquee effect
      // Top padding positions first line at bottom, bottom padding allows last line to reach top
      // Only set padding if not already set (first time playing)
      if (previewRef.current && previewContentRef.current) {
        const containerHeight = previewRef.current.clientHeight;
        const currentPaddingTop = previewContentRef.current.style.paddingTop;

        // Only set padding if it's not already set (first time playing)
        if (
          !currentPaddingTop ||
          currentPaddingTop === "1rem" ||
          currentPaddingTop === ""
        ) {
          previewContentRef.current.style.paddingTop = `${containerHeight}px`;
          previewContentRef.current.style.paddingBottom = `${containerHeight}px`;
          if (!hasStartedPlayingRef.current) {
            previewRef.current.scrollTop = 0;
          }
        }
      }

      scrollIntervalRef.current = setInterval(() => {
        if (previewRef.current) {
          const currentScroll = previewRef.current.scrollTop;
          const maxScroll =
            previewRef.current.scrollHeight - previewRef.current.clientHeight;

          if (currentScroll >= maxScroll) {
            // Last line has reached the top, loop back to start (infinite marquee)
            previewRef.current.scrollTop = 0;
          } else {
            // Scroll down (increase scrollTop) - content moves up, creating marquee effect
            // Use smaller increments for smoother scrolling
            previewRef.current.scrollTop = Math.min(
              maxScroll,
              currentScroll + scrollAmount
            );
          }
        }
      }, intervalTime);
    } else if (!isPlaying && previewContentRef.current) {
      // When paused, keep padding and scroll position - don't reset
      // Clean up interval when paused
      if (scrollIntervalRef.current) {
        clearInterval(scrollIntervalRef.current);
        scrollIntervalRef.current = null;
      }
    }

    // Cleanup on unmount
    return () => {
      if (scrollIntervalRef.current) {
        clearInterval(scrollIntervalRef.current);
      }
    };
  }, [isPlaying, formData.speed]);

  const handleSave = async () => {
    if (!formData.elementName && !name) {
      toast.error("Please enter an element name");
      return;
    }
    if (!folderId) {
      toast.error("Please select a folder");
      return;
    }

    try {
      const saveData = {
        folderId,
        name: formData.elementName || name,
        settings: JSON.stringify(formData),
        type: "prompter",
      };

      // Include id when updating existing element
      if (updatePresetId) {
        saveData.id = updatePresetId;
      }

      await createPrompter(saveData);
      toast.success(
        updatePresetId
          ? "Prompter updated successfully"
          : "Prompter created successfully"
      );

      // Reset state after successful save
      setSelectedLayout("");
      setSelectedTheme("");
      setHasConfirmedSelection(false);
      hasStartedPlayingRef.current = false;
      setIsPlaying(false);

      // Clear update preset ID after successful save
      if (updatePresetId) {
        setUpdatePresetId("");
      }

      navigate(`/dashboard/folders/element/${folderId}`);
    } catch (error) {
      toast.error(
        updatePresetId ? "Error updating prompter" : "Error creating prompter"
      );
      logger("error", "Error saving:", error);
    }
  };

  const fonts = [
    "Helvetica",
    "Arial",
    "Times New Roman",
    "Courier New",
    "Georgia",
    "Verdana",
  ];

  const handleElementTypeChange = (elementType) => {
    setSelectedCreateElement(elementType);
    if (folderId) {
      navigate(`/dashboard/elemets/${folderId}`);
    }
  };

  // Show loading state when editing (data is being loaded)
  if (isLoading) {
    return (
      <div className="flex-1 flex items-center justify-center bg-[#28242E]">
        <div className="text-white">Loading...</div>
      </div>
    );
  }

  // Show selection screen when selection is not confirmed AND not editing
  // Similar to CallToAction: selectedTemplateKey === "" shows ChooseCtaTemplate
  // When editing (updatePresetId exists), skip selection screen and go directly to editor
  if (
    !updatePresetId &&
    (!hasConfirmedSelection || !selectedLayout || !selectedTheme)
  ) {
    return (
      <div className="flex-1 flex flex-col lg:flex-row bg-[#28242E] h-full overflow-hidden">
        {/* Left Sidebar */}
        <div className="w-full lg:w-64 bg-black border-b lg:border-b-0 lg:border-r border-gray-800 flex lg:flex-col flex-row overflow-x-auto">
          <div className="p-2 lg:p-4 flex lg:flex-col flex-row gap-1 lg:space-y-1 flex-1 min-w-0">
            <button
              role="button"
              onClick={() => handleElementTypeChange("cta")}
              className={`px-3 lg:px-4 py-2 lg:py-3 text-base font-medium transition-colors cursor-pointer whitespace-nowrap text-start ${
                false
                  ? "bg-[#360083] text-white"
                  : "text-gray-400 hover:text-white hover:bg-gray-900"
              }`}
            >
              Banner
            </button>
            <button
              role="button"
              onClick={() => handleElementTypeChange("website")}
              className={`px-3 lg:px-4 py-2 lg:py-3 text-base font-medium transition-colors cursor-pointer whitespace-nowrap text-start ${
                false
                  ? "bg-[#360083] text-white"
                  : "text-gray-400 hover:text-white hover:bg-gray-900"
              }`}
            >
              Webs
            </button>
            <button
              role="button"
              onClick={() => handleElementTypeChange("media_file")}
              className={`px-3 lg:px-4 py-2 lg:py-3 text-base font-medium transition-colors cursor-pointer whitespace-nowrap text-start ${
                false
                  ? "bg-[#360083] text-white"
                  : "text-gray-400 hover:text-white hover:bg-gray-900"
              }`}
            >
              Videos
            </button>
            <button
              role="button"
              onClick={() => handleElementTypeChange("document_file")}
              className={`px-3 lg:px-4 py-2 lg:py-3 text-base font-medium transition-colors cursor-pointer whitespace-nowrap text-start ${
                false
                  ? "bg-[#360083] text-white"
                  : "text-gray-400 hover:text-white hover:bg-gray-900"
              }`}
            >
              Slides
            </button>
            <button
              role="button"
              onClick={() => handleElementTypeChange("prompter")}
              className={`px-3 lg:px-4 py-2 lg:py-3 text-base font-medium transition-colors cursor-pointer whitespace-nowrap text-start ${
                true
                  ? "bg-[#360083] text-white"
                  : "text-gray-400 hover:text-white hover:bg-gray-900"
              }`}
            >
              Prompter
            </button>
          </div>
        </div>

        {/* Main Content Area */}
        <div className="flex-1 overflow-y-auto p-4 md:p-6 lg:p-8">
          <div className="flex flex-col gap-6 md:gap-8 max-w-4xl mx-auto w-full">
            {/* Layout Options */}
            <div>
              <h3 className="text-white text-base md:text-lg font-semibold mb-3 md:mb-4">
                Layout
              </h3>
              <div className="flex flex-col sm:flex-row gap-3 md:gap-4">
                <div
                  onClick={() => handleLayoutSelect("compact")}
                  className={`cursor-pointer transition-all ${
                    selectedLayout === "compact"
                      ? "ring-2 ring-[#8800FF] rounded"
                      : "opacity-60 hover:opacity-100"
                  }`}
                >
                  <div className="p-2 md:p-2">
                    <img
                      src="/icons/compact.svg"
                      alt="Compact layout"
                      className="w-full max-w-[120px] sm:max-w-[140px] md:w-32 h-auto mx-auto"
                    />
                  </div>
                  <span className="text-white text-sm font-medium block text-center mt-2">
                    Compact
                  </span>
                </div>
                <div
                  onClick={() => handleLayoutSelect("wide")}
                  className={`cursor-pointer transition-all ${
                    selectedLayout === "wide"
                      ? "ring-2 ring-[#8800FF] rounded"
                      : "opacity-60 hover:opacity-100"
                  }`}
                >
                  <div className="p-2 md:p-2">
                    <img
                      src="/icons/wide.svg"
                      alt="Wide layout"
                      className="w-full max-w-[120px] sm:max-w-[140px] md:w-32 h-auto mx-auto"
                    />
                  </div>
                  <span className="text-white text-sm font-medium block text-center mt-2">
                    Wide
                  </span>
                </div>
              </div>
            </div>

            {/* Theme Options */}
            <div>
              <h3 className="text-white text-base md:text-lg font-semibold mb-3 md:mb-4">
                Theme
              </h3>
              <div className="flex flex-col sm:flex-row gap-3 md:gap-4">
                <div
                  onClick={() => handleThemeSelect("light")}
                  className={`cursor-pointer transition-all ${
                    selectedTheme === "light"
                      ? "ring-2 ring-[#8A2BE2] ring-offset-2 rounded"
                      : "opacity-60 hover:opacity-100"
                  }`}
                >
                  <div className="p-2 md:p-2">
                    <div
                      className={`w-full max-w-[280px] sm:max-w-[240px] md:w-64 h-36 sm:h-40 md:h-48 bg-white rounded-lg p-3 md:p-4 shadow-lg mx-auto`}
                    >
                      <p className="text-black text-xs md:text-sm leading-relaxed">
                        {(selectedLayout || "compact") === "wide"
                          ? "Welcome, everyone, to today's presentation on innovative project management techniques that can transform the way we work together. Over the next few minutes, we'll explore three key strategies: first,"
                          : "Welcome, everyone, to today's presentation on innovative project management techniques that can transform the way we work together. Over the next few minutes,"}
                      </p>
                    </div>
                  </div>
                  <span className="text-white text-sm font-medium block text-center mt-2">
                    Light
                  </span>
                </div>
                <div
                  onClick={() => handleThemeSelect("dark")}
                  className={`cursor-pointer transition-all ${
                    selectedTheme === "dark"
                      ? "ring-2 ring-[#8A2BE2] ring-offset-2 rounded"
                      : "opacity-60 hover:opacity-100"
                  }`}
                >
                  <div className="p-2 md:p-2">
                    <div
                      className={`w-full max-w-[280px] sm:max-w-[240px] md:w-64 h-36 sm:h-40 md:h-48 bg-black rounded-lg p-3 md:p-4 shadow-lg mx-auto`}
                    >
                      <p className="text-white bg-black text-xs md:text-sm leading-relaxed">
                        {(selectedLayout || "compact") === "wide"
                          ? "Welcome, everyone, to today's presentation on innovative project management techniques that can transform the way we work together. Over the next few minutes, we'll explore three key strategies: first,"
                          : "Welcome, everyone, to today's presentation on innovative project management techniques that can transform the way we work together. Over the next few minutes,"}
                      </p>
                    </div>
                  </div>
                  <span className="text-white text-sm font-medium block text-center mt-2">
                    Dark
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show editor when layout and theme are selected or when editing
  // Similar to CallToAction: selectedTemplateKey !== "" shows EditCtaTemplate

  return (
    <div className="flex-1 flex flex-col md:flex-row bg-[#28242E] h-full overflow-hidden">
      {/* Left Preview Area */}
      <div className="w-full md:w-1/2 bg-[#898989] p-4 md:p-8 flex items-center justify-center">
        <div
          ref={previewRef}
          className={`w-full h-full shadow-lg overflow-y-auto ${
            formData.theme === "dark" ? "bg-gray-900" : "bg-white"
          }`}
          style={{
            scrollBehavior: isPlaying ? "auto" : "smooth",
          }}
        >
          <div
            ref={previewContentRef}
            style={{
              paddingTop: "1rem",
              paddingBottom: "1rem",
              paddingLeft: "1rem",
              paddingRight: "1rem",
            }}
          >
            <p
              className="text-sm md:text-base"
              style={{
                fontFamily: formData.font,
                fontSize: `${formData.fontSize}px`,
                color: formData.textColor,
                fontWeight: formData.isBold ? "bold" : "normal",
                fontStyle: formData.isItalic ? "italic" : "normal",
                textAlign: formData.textAlign || "left",
                lineHeight: `${formData.fontSize * 1.7}px`,
                letterSpacing: "0.01em",
              }}
            >
              {formData.text ||
                "Type your script here... your changes will instantly appear in the preview."}
            </p>
          </div>
        </div>
      </div>

      {/* Right Edit Controls */}
      <div className="w-full md:w-1/2 bg-[#28242E] p-4 md:p-6 overflow-y-auto">
        <div className="flex items-center justify-between mb-4 md:mb-6">
          <h2 className="text-white text-lg md:text-xl font-semibold">
            EDIT YOUR ELEMENT
          </h2>
        </div>

        {/* Text Input */}
        <div className="mb-4 md:mb-6">
          <label className="text-white text-sm font-medium mb-2 block">
            Text
          </label>
          <textarea
            value={formData.text}
            onChange={(e) => setFormData({ ...formData, text: e.target.value })}
            placeholder="Type your script here... your changes will instantly appear in the preview."
            className="w-full bg-white rounded-lg p-3 md:p-4 text-black min-h-[200px] md:min-h-[320px] resize-none focus:outline-none focus:ring-2 focus:ring-[#8A2BE2] text-sm md:text-base"
          />
        </div>

        {/* Font, Formatting, and Font Size Row */}
        <div className="mb-4 md:mb-6 flex flex-col sm:flex-row sm:items-center gap-3 md:gap-4">
          {/* Font Controls */}
          <div className="flex items-center gap-2">
            <label className="text-white text-sm font-medium whitespace-nowrap">
              Font
            </label>
            <select
              value={formData.font}
              onChange={(e) =>
                setFormData({ ...formData, font: e.target.value })
              }
              className="bg-white rounded-lg p-2 text-black focus:outline-none focus:ring-2 focus:ring-[#8A2BE2] min-w-[120px] text-sm"
            >
              {fonts.map((font) => (
                <option key={font} value={font}>
                  {font}
                </option>
              ))}
            </select>
          </div>

          {/* Formatting Buttons */}
          <div className="flex gap-2">
            <button
              onClick={() =>
                setFormData({ ...formData, isBold: !formData.isBold })
              }
              className={`p-2 rounded ${
                formData.isBold
                  ? "bg-[#8A2BE2] text-white"
                  : "bg-gray-700 text-gray-300 hover:bg-gray-600 border border-white"
              }`}
            >
              <FaBold />
            </button>
            <button
              onClick={() =>
                setFormData({ ...formData, isItalic: !formData.isItalic })
              }
              className={`p-2 rounded ${
                formData.isItalic
                  ? "bg-[#8A2BE2] text-white"
                  : "bg-gray-700 text-gray-300 hover:bg-gray-600 border border-white"
              }`}
            >
              <FaItalic />
            </button>
            <button
              onClick={() => handleTextAlign("left")}
              className={`p-2 rounded ${
                formData.textAlign === "left"
                  ? "bg-[#8A2BE2] text-white"
                  : "bg-gray-700 text-gray-300 hover:bg-gray-600 border border-white"
              }`}
            >
              <FaAlignLeft />
            </button>
            <button
              onClick={() => handleTextAlign("center")}
              className={`p-2 rounded ${
                formData.textAlign === "center"
                  ? "bg-[#8A2BE2] text-white"
                  : "bg-gray-700 text-gray-300 hover:bg-gray-600 border border-white"
              }`}
            >
              <FaAlignCenter />
            </button>
          </div>

          {/* Font Size */}
          <div className="flex items-center gap-2">
            <label className="text-white text-sm font-medium whitespace-nowrap">
              Font Size
            </label>
            <input
              type="number"
              value={formData.fontSize}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  fontSize: parseInt(e.target.value) || 23,
                })
              }
              className="w-20 bg-white rounded-lg p-2 text-black focus:outline-none focus:ring-2 focus:ring-[#8A2BE2] text-sm"
            />
          </div>
        </div>

        {/* Speed, Text Color, and Play Button Row */}
        <div className="mb-4 md:mb-6 flex flex-col sm:flex-row sm:items-center gap-3 md:gap-4">
          {/* Speed Slider */}
          <div className="flex-1 w-full">
            <label className="text-white text-sm font-medium mb-2 block">
              Speed
            </label>
            <input
              type="range"
              min="0"
              max="100"
              value={formData.speed}
              onChange={(e) =>
                setFormData({ ...formData, speed: parseInt(e.target.value) })
              }
              className="w-full accent-[#8A2BE2]"
            />
            <div className="text-gray-400 text-xs mt-1">{formData.speed}</div>
          </div>

          {/* Text Color */}
          <div className="flex items-center gap-2">
            <label className="text-white text-sm font-medium whitespace-nowrap">
              Text Color
            </label>
            <input
              type="color"
              value={formData.textColor}
              onChange={(e) =>
                setFormData({ ...formData, textColor: e.target.value })
              }
              className="w-8 h-8 rounded-lg cursor-pointer"
            />
          </div>

          {/* Play Button */}
          <button
            onClick={handlePlayPause}
            disabled={formData.speed === 0}
            className={`border border-[#1CD760] text-[#1CD760] px-4 md:px-6 py-2 md:py-3 rounded-lg font-semibold hover:bg-[#1CD760] hover:text-white transition-colors flex items-center justify-center gap-2 whitespace-nowrap w-full sm:w-auto ${
              formData.speed === 0 ? "opacity-50 cursor-not-allowed" : ""
            }`}
          >
            {isPlaying ? <Pause size={20} /> : <Play size={20} />}
            {isPlaying ? "Pause" : "Play"}
          </button>
        </div>

        {/* Element Name and Save Button Row */}
        <div className="flex flex-col sm:flex-row gap-3 md:gap-4">
          {/* Element Name */}
          <div className="flex-1 w-full">
            <label className="text-white text-sm font-medium mb-2 block">
              Element Name
            </label>
            <input
              type="text"
              value={formData.elementName}
              onChange={(e) =>
                setFormData({ ...formData, elementName: e.target.value })
              }
              placeholder="e.g., Launch Prompter"
              className="w-full bg-white rounded-lg p-3 text-black focus:outline-none focus:ring-2 focus:ring-[#8A2BE2] text-sm"
            />
            <p className="text-gray-400 text-xs mt-1">
              You can change it later.
            </p>
          </div>

          {/* Save Button */}
          <div className="flex items-end sm:items-start">
            <div className="w-full sm:w-auto">
              <label className="text-white text-sm font-medium mb-2 block sm:invisible sm:h-0">
                &nbsp;
              </label>
              <button
                onClick={handleSave}
                className="bg-[#1CD760] mt-[18px] text-white px-6 md:px-12 py-3 rounded-xl font-semibold hover:bg-[#12a043] transition-colors whitespace-nowrap w-full sm:w-auto"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
